import React from 'react';

/**
 * QuestionTrack
 * Renders only the active question card; simulates vertical motion using
 * CSS-based enter/exit transitions (fade + translate). No native scrolling.
 *
 * Props:
 *  - currentIndex: number
 *  - renderCard: (index: number) => React.ReactNode
 *  - onEnter?: (index: number) => void
 *  - onExit?: (index: number) => void
 *  - prefersReducedMotion?: boolean
 */
export default function QuestionTrack({
  currentIndex = 0,
  renderCard,
  onEnter,
  onExit,
  prefersReducedMotion = false,
}) {
  const [activeIndex, setActiveIndex] = React.useState(currentIndex);
  const [prevIndex, setPrevIndex] = React.useState(null);
  const [phase, setPhase] = React.useState('idle'); // 'idle' | 'entering' | 'exiting'

  React.useEffect(() => {
    if (currentIndex === activeIndex) return;
    setPrevIndex(activeIndex);
    setActiveIndex(currentIndex);
      setPhase('exiting');
      if (typeof onExit === 'function') {
        try { onExit(activeIndex); } catch (_) {}
      }
    const t = setTimeout(() => {
        setPhase('entering');
        if (typeof onEnter === 'function') {
          try { onEnter(currentIndex); } catch (_) {}
        }
      const t2 = setTimeout(() => {
        setPrevIndex(null);
        setPhase('idle');
      }, prefersReducedMotion ? 0 : 300);
      return () => clearTimeout(t2);
    }, prefersReducedMotion ? 0 : 240);
    return () => clearTimeout(t);
  }, [currentIndex, activeIndex, onEnter, onExit, prefersReducedMotion]);

  const renderWithPhase = (index, key, state) => {
    const base = 'w-full h-full';
    if (prefersReducedMotion) {
      // No animation; render active immediately
      return (
        <div key={key} className={base} style={{ position: 'relative' }}>
          {renderCard(index)}
        </div>
      );
    }
    const cls =
      state === 'enter'
        ? 'card-enter card-enter-active'
        : state === 'exit'
        ? 'card-exit card-exit-active'
        : '';
    return (
      <div key={key} className={`${base} ${cls}`} style={{ position: 'absolute', inset: 0 }}>
        {renderCard(index)}
      </div>
    );
  };

  return (
    <div className="relative" style={{ height: '100%' }}>
      {/* Previous card (exiting) */}
      {prevIndex !== null && phase !== 'idle' && renderWithPhase(prevIndex, `prev-${prevIndex}`, 'exit')}
      {/* Active card (entering/idle) */}
      {renderWithPhase(activeIndex, `active-${activeIndex}`, phase === 'entering' ? 'enter' : 'idle')}
    </div>
  );
}